import React, { useState, useRef, useEffect } from 'react';
import { parse } from 'papaparse';

const NameList = ({ names, setNames }) => {
  const [newName, setNewName] = useState('');
  const [bulkNames, setBulkNames] = useState('');
  const [inputMode, setInputMode] = useState('single');
  const [error, setError] = useState('');
  const [isBulkActionsVisible, setIsBulkActionsVisible] = useState(false);
  const contentRef = useRef(null);

  useEffect(() => {
    function handleClickOutside(event) {
      if (contentRef.current && !contentRef.current.contains(event.target)) {
        setIsBulkActionsVisible(false); // Hide content if clicked outside
      }
    }

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  // Handle file upload
  const handleFileUpload = async (file) => {
    try {
      const text = await file.text();
      const results = parse(text, {
        header: false,
        skipEmptyLines: true
      });

      const uploadedNames = results.data
        .flat()
        .map(name => name.trim())
        .filter(name => name.length > 0);

      if (uploadedNames.length === 0) {
        throw new Error('File contains no valid names');
      }

      setNames(prev => [...new Set([...prev, ...uploadedNames])]);
      setError('');
    } catch (err) {
      setError(`Error processing file: ${err.message}`);
    }
  };

  // Bulk actions
  const bulkActions = {
    sortAZ: () => setNames(prev => [...prev].sort((a, b) => a.localeCompare(b))),
    sortZA: () => setNames(prev => [...prev].sort((a, b) => b.localeCompare(a))),
    clearAll: () => setNames([]),
    removeDuplicates: () => setNames(prev => [...new Set(prev)]),
  };

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <h3 className="text-lg font-medium text-gray-900">
          Guest Names ({names.length})
        </h3>
        
        {/* Bulk Actions Dropdown */}
        <div ref={contentRef} className="relative">
          <button
            className="px-3 py-1.5 text-sm bg-gray-100 hover:bg-gray-200 rounded-md"
            onClick={() => setIsBulkActionsVisible(!isBulkActionsVisible)}
          >
            <span className='flex items-center'>Bulk Actions <svg width="20" height="20" viewBox="0 0 24 25" fill="none" xmlns="http://www.w3.org/2000/svg">
<path d="M5.75002 9.75C5.44668 9.75 5.1732 9.93273 5.05711 10.213C4.94103 10.4932 5.00519 10.8158 5.21969 11.0303L11.4697 17.2803C11.6103 17.421 11.8011 17.5 12 17.5C12.1989 17.5 12.3897 17.421 12.5304 17.2803L18.7804 11.0303C18.9949 10.8158 19.059 10.4932 18.9429 10.213C18.8268 9.93273 18.5534 9.75 18.25 9.75H5.75002Z" fill="#323544"/>
</svg></span>

          </button>
          
            {isBulkActionsVisible && (
          <div className="absolute right-0 mt-1 w-48 bg-white border rounded-lg shadow-lg py-1 z-10">
            <button
              onClick={bulkActions.sortAZ}
              className="block w-full px-4 py-2 text-sm text-left hover:bg-gray-100"
            >
              Sort A → Z
            </button>
            <button
              onClick={bulkActions.sortZA}
              className="block w-full px-4 py-2 text-sm text-left hover:bg-gray-100"
            >
              Sort Z → A
            </button>
            <button
              onClick={bulkActions.removeDuplicates}
              className="block w-full px-4 py-2 text-sm text-left hover:bg-gray-100"
            >
              Remove Duplicates
            </button>
            <button
              onClick={bulkActions.clearAll}
              className="block w-full px-4 py-2 text-sm text-left text-red-600 hover:bg-red-50"
            >
              Clear All
            </button>
          </div>
            )}
        </div>
      </div>

      {/* Input Mode Tabs */}
      <div className="flex gap-2 border-b pb-2">
        {['single', 'bulk', 'file'].map((mode) => (
          <button
            key={mode}
            onClick={() => setInputMode(mode)}
            className={`px-4 py-2 rounded-t-md capitalize ${
              inputMode === mode
                ? 'bg-primary text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            {mode === 'file' ? 'File Upload' : `${mode} Entry`}
          </button>
        ))}
      </div>

      {/* Input Sections */}
      {inputMode === 'single' ? (
        <form onSubmit={(e) => e.preventDefault()}>
          <div className="flex gap-2">
            <input
              type="text"
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              placeholder="Enter guest name"
              className="flex-1 rounded-md border-gray-300 shadow-sm focus:border-primary focus:ring-primary"
            />
            <button
              onClick={() => {
                if (newName.trim()) {
                  setNames(prev => [...prev, newName.trim()]);
                  setNewName('');
                }
              }}
              className="px-4 py-2 bg-primary hover:bg-primary-dark text-white rounded-md"
            >
              Add
            </button>
          </div>
        </form>
      ) : inputMode === 'bulk' ? (
        <div className="space-y-4">
          <textarea
            value={bulkNames}
            onChange={(e) => setBulkNames(e.target.value)}
            placeholder="Paste names separated by lines or commas"
            rows="4"
            className="w-full rounded-md border-gray-300 shadow-sm focus:border-primary focus:ring-primary"
          />
          <div className="flex justify-between items-center">
            <span className="text-sm text-gray-500">
              {bulkNames.split(/[\n,]/).filter(l => l.trim()).length} names detected
            </span>
            <button
              onClick={() => {
                const namesArray = bulkNames
                  .split(/[\n,]/)
                  .map(name => name.trim())
                  .filter(name => name);
                
                if (namesArray.length > 0) {
                  setNames(prev => [...new Set([...prev, ...namesArray])]);
                  setBulkNames('');
                }
              }}
              className="px-4 py-2 bg-primary hover:bg-primary-dark text-white rounded-md"
            >
              Import Names
            </button>
          </div>
        </div>
      ) : (
        <div className="border-2 border-dashed border-gray-300 rounded-md p-4 text-center">
          <input
            type="file"
            accept=".csv,.txt"
            onChange={(e) => handleFileUpload(e.target.files[0])}
            className="hidden"
            id="fileUpload"
          />
          <label htmlFor="fileUpload" className="cursor-pointer space-y-2">
            <div className="mx-auto w-12 h-12 text-gray-400">
              <svg className="w-full h-full" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
              </svg>
            </div>
            <p className="text-sm text-gray-600">
              Drag & drop CSV/TXT file or click to upload
            </p>
            <p className="text-xs text-gray-500">
              Supported formats: One name per line or CSV column
            </p>
          </label>
        </div>
      )}

      {/* Error Messages */}
      {error && (
        <div className="p-3 bg-red-50 text-red-700 rounded-md text-sm">
          ⚠️ {error}
        </div>
      )}

      {/* Names List */}
      <div className="border rounded-lg divide-y divide-gray-200 max-h-96 overflow-y-auto">
        {names.map((name, index) => (
          <div key={index} className="flex items-center justify-between px-4 py-3">
            <span className="text-gray-700">{name}</span>
            <button
              onClick={() => setNames(prev => prev.filter((_, i) => i !== index))}
              className="text-red-500 hover:text-red-700"
            >
              ×
            </button>
          </div>
        ))}
        
        {names.length === 0 && (
          <div className="p-4 text-center text-gray-500">
            No names added yet
          </div>
        )}
      </div>
    </div>
  );
};

export default NameList;